/**
 * Il cuore del dominio: l'azione.
 *
 * Ogni scrittura sul PMS è preceduta da una riga `actions`. L'agente non
 * scrive: propone. Il payload descrive *cosa* cambiare, l'anteprima descrive
 * *cosa vedrà l'host*, la ricevuta descrive *cosa è arrivato sui canali*.
 *
 * L'anteprima contiene stringhe già formattate. È voluto: una proposta
 * approvata è un documento storico, e deve restare leggibile identica anche
 * se un anno dopo cambiamo il modo di formattare i prezzi.
 */

import { z } from "zod";

// ── Vocabolario ──────────────────────────────────────────────────────────────

export const CHANNELS = [
  "guesty",
  "airbnb",
  "booking",
  "vrbo",
  "diretto",
] as const;
export type Channel = (typeof CHANNELS)[number];
export const ChannelSchema = z.enum(CHANNELS);

export const CHANNEL_LABELS: Record<Channel, string> = {
  guesty: "Guesty",
  airbnb: "Airbnb",
  booking: "Booking.com",
  vrbo: "Vrbo",
  diretto: "Sito diretto",
};

/** Famiglia di scrittura sul PMS. */
export const ACTION_KINDS = [
  "calendario",
  "contenuto_annuncio",
  "messaggio_ospite",
  "task",
] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

/**
 * Granularità con cui si misura l'autonomia (OTTO.md §7.3): si può essere
 * autonomi sui messaggi wifi restando in "propone" sui prezzi.
 */
export const ACTION_CATEGORIES = [
  "prezzo",
  "min_notti",
  "disponibilita",
  "contenuto_annuncio",
  "messaggio_ospite",
  "task",
] as const;
export type ActionCategory = (typeof ACTION_CATEGORIES)[number];
export const ActionCategorySchema = z.enum(ACTION_CATEGORIES);

export const CATEGORY_LABELS: Record<ActionCategory, string> = {
  prezzo: "Prezzi",
  min_notti: "Minimo notti",
  disponibilita: "Disponibilità",
  contenuto_annuncio: "Contenuti annuncio",
  messaggio_ospite: "Messaggi ospiti",
  task: "Task operativi",
};

export const ACTION_STATUSES = [
  "proposta",
  "approvata",
  "in_esecuzione",
  "eseguita",
  "fallita",
  "annullata",
  "rollback",
] as const;
export type ActionStatus = (typeof ACTION_STATUSES)[number];
export const ActionStatusSchema = z.enum(ACTION_STATUSES);

/** Stati in cui l'azione ha già toccato (o sta toccando) il gestionale. */
export const TERMINAL_STATUSES: readonly ActionStatus[] = [
  "eseguita",
  "fallita",
  "annullata",
  "rollback",
];

export const AUTONOMY_GEARS = ["propone", "esegue", "autonomo"] as const;
export type AutonomyGear = (typeof AUTONOMY_GEARS)[number];

/**
 * Soglie di sblocco delle marce superiori (OTTO.md §7.3).
 * Stanno qui perché sono una promessa fatta all'host, non un dettaglio del
 * backend: la schermata delle impostazioni le mostra parola per parola.
 */
export const AUTONOMY_UNLOCK_RATE = 0.85;
export const AUTONOMY_UNLOCK_MIN_ACTIONS = 30;

// ── Payload ──────────────────────────────────────────────────────────────────

const IsoDateSchema = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, "data nel formato AAAA-MM-GG");

export const DayAvailabilitySchema = z.enum(["available", "unavailable"]);
export type DayAvailability = z.infer<typeof DayAvailabilitySchema>;

/**
 * Modifica di una singola notte. I campi assenti non vengono toccati.
 *
 * Le percentuali ("alza del 15%") sono risolte in prezzi assoluti *al momento
 * della proposta*, leggendo lo stato reale: così l'host approva un numero, non
 * una formula, e il rollback è esatto.
 */
export const CalendarDayChangeSchema = z
  .object({
    date: IsoDateSchema,
    priceCents: z.number().int().min(0).optional(),
    minNights: z.number().int().min(1).max(365).optional(),
    status: DayAvailabilitySchema.optional(),
  })
  .refine(
    (d) =>
      d.priceCents !== undefined ||
      d.minNights !== undefined ||
      d.status !== undefined,
    { message: "ogni giorno deve modificare almeno un campo" },
  );
export type CalendarDayChange = z.infer<typeof CalendarDayChangeSchema>;

export const CalendarPayloadSchema = z.object({
  kind: z.literal("calendario"),
  items: z
    .array(
      z.object({
        listingId: z.string().min(1),
        days: z.array(CalendarDayChangeSchema).min(1),
      }),
    )
    .min(1),
});
export type CalendarPayload = z.infer<typeof CalendarPayloadSchema>;

/** Campi testuali dell'annuncio scrivibili via PMS. Il titolo ha un limite duro. */
export const LISTING_TEXT_FIELDS = [
  "title",
  "summary",
  "space",
  "access",
  "neighborhood",
  "transit",
  "notes",
  "houseRules",
  "interactionWithGuests",
] as const;
export type ListingTextField = (typeof LISTING_TEXT_FIELDS)[number];

export const LISTING_FIELD_LABELS: Record<ListingTextField, string> = {
  title: "Titolo",
  summary: "Descrizione",
  space: "Lo spazio",
  access: "Accesso",
  neighborhood: "Quartiere",
  transit: "Come arrivare",
  notes: "Note",
  houseRules: "Regole della casa",
  interactionWithGuests: "Interazione con gli ospiti",
};

/** Airbnb tronca oltre i 50 caratteri e Guesty rifiuta: si valida qui, a monte. */
export const LISTING_TITLE_MAX_LENGTH = 50;

export const ListingFieldsSchema = z.object({
  /**
   * Il tetto di 50 caratteri di Airbnb **non** sta qui.
   *
   * Ci stava, e rendeva irraggiungibile il guardrail `titolo_troppo_lungo`:
   * un titolo lungo veniva rifiutato dallo schema prima di arrivare al
   * controllo, quindi l'host vedeva un errore di validazione invece della
   * frase che spiega quanti caratteri sono e perché non si può. Il guardrail
   * era codice morto e nessuno se n'era accorto.
   *
   * Chi decide sui limiti dei canali è `guardrails.ts`, in un posto solo. Qui
   * resta un tetto tecnico largo, per non far entrare in memoria un romanzo.
   */
  title: z.string().min(1).max(500).optional(),
  summary: z.string().max(10_000).optional(),
  space: z.string().max(10_000).optional(),
  access: z.string().max(10_000).optional(),
  neighborhood: z.string().max(10_000).optional(),
  transit: z.string().max(10_000).optional(),
  notes: z.string().max(10_000).optional(),
  houseRules: z.string().max(10_000).optional(),
  interactionWithGuests: z.string().max(10_000).optional(),
});
export type ListingFields = z.infer<typeof ListingFieldsSchema>;

export const ListingContentPayloadSchema = z.object({
  kind: z.literal("contenuto_annuncio"),
  mode: z.enum(["sostituisci", "aggiungi_in_coda"]),
  items: z
    .array(
      z.object({
        listingId: z.string().min(1),
        fields: ListingFieldsSchema,
      }),
    )
    .min(1),
});
export type ListingContentPayload = z.infer<typeof ListingContentPayloadSchema>;

export const GuestMessagePayloadSchema = z.object({
  kind: z.literal("messaggio_ospite"),
  conversationId: z.string().min(1),
  testo: z.string().min(1).max(4000),
  categoria: z.string().min(1),
  /**
   * La scheda del quaderno da cui è uscita la bozza, quando ce n'è una.
   *
   * Sta nel payload e non in un log: è quello che rende misurabile il tasso di
   * accettazione. Senza, «l'host ha approvato» e «l'host ha approvato una
   * risposta che avevo preparato io dal quaderno» sono la stessa riga, e la
   * seconda è l'unica che dice se il quaderno serve.
   *
   * Assente vuol dire che Otto ha scritto senza una scheda: è un caso lecito
   * — una domanda nuova — e va contato a parte, perché è la copertura.
   */
  scheda: z.string().min(1).optional(),
});
export type GuestMessagePayload = z.infer<typeof GuestMessagePayloadSchema>;

export const TASK_STATUSES = ["da_fare", "in_corso", "completato"] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const TaskPayloadSchema = z.object({
  kind: z.literal("task"),
  op: z.enum(["crea", "aggiorna"]),
  taskId: z.string().optional(),
  listingId: z.string().min(1),
  titolo: z.string().min(1).max(200),
  assegnatoA: z.string().max(120).optional(),
  scadenza: z.string().datetime({ offset: true }).optional(),
  stato: z.enum(TASK_STATUSES).optional(),
});
export type TaskPayload = z.infer<typeof TaskPayloadSchema>;

export const ActionPayloadSchema = z.discriminatedUnion("kind", [
  CalendarPayloadSchema,
  ListingContentPayloadSchema,
  GuestMessagePayloadSchema,
  TaskPayloadSchema,
]);
export type ActionPayload = z.infer<typeof ActionPayloadSchema>;

// ── Anteprima ────────────────────────────────────────────────────────────────

export const WARNING_CODES = [
  "prenotazioni_confermate",
  "floor_price",
  "nessun_minimo",
  "tetto_giornaliero",
  "kill_switch",
  /**
   * L'account è sospeso: abbonamento non pagato, o cancellazione in corso. Si
   * controlla anche qui, e non solo all'ingresso delle richieste, perché il
   * worker non passa dall'ingresso: un'azione approvata prima della
   * sospensione, eseguita dopo, non deve arrivare al gestionale.
   */
  "account_sospeso",
  "azione_vietata",
  "orizzonte_date",
  "troppi_immobili",
  "troppe_notti",
  "titolo_troppo_lungo",
  "campo_non_scrivibile",
  "nessun_cambiamento",
  // «Non c'è niente da cambiare» e «di quelle notti non so niente» sono
  // opposte: la prima rassicura, la seconda ammette. Un codice a testa.
  "notti_sconosciute",
  "canale_non_supporta",
  /** Un prezzo a zero o sotto: non è un prezzo, è un errore di calcolo. */
  "prezzo_non_valido",
  /** Il testo contiene segnaposto tipo `[nome]` o `{{…}}`: non è finito. */
  "testo_incompleto",
  /** Sulla stessa conversazione è già partito un messaggio da poco. */
  "messaggio_doppio",
  /**
   * La modifica tocca notti che l'host ha bloccato a mano sul gestionale.
   * Si fa lo stesso — un listino si sistema anche in anticipo — ma va detto:
   * un prezzo su una notte bloccata non la mette in vendita.
   */
  "notti_bloccate",
  /**
   * La modifica tocca notti già prenotate. Si fa — se la prenotazione si
   * cancella il prezzo nuovo conta — ma la prenotazione non cambia, e la card
   * lo dice con il nome dell'ospite.
   */
  "notti_gia_vendute",
] as const;
export type WarningCode = (typeof WARNING_CODES)[number];

export const WARNING_SEVERITIES = ["info", "attenzione", "blocco"] as const;
export type WarningSeverity = (typeof WARNING_SEVERITIES)[number];

export const PreviewWarningSchema = z.object({
  code: z.enum(WARNING_CODES),
  severity: z.enum(WARNING_SEVERITIES),
  message: z.string(),
});
export type PreviewWarning = z.infer<typeof PreviewWarningSchema>;

export const PreviewLineSchema = z.object({
  /** Chiave tecnica, usata dalla UI per l'icona. */
  field: z.string(),
  label: z.string(),
  /** Valore attuale, già formattato. `null` = non impostato. */
  from: z.string().nullable(),
  to: z.string(),
  nights: z.number().int().optional(),
  dateFrom: IsoDateSchema.optional(),
  dateTo: IsoDateSchema.optional(),
});
export type PreviewLine = z.infer<typeof PreviewLineSchema>;

export const PreviewTargetSchema = z.object({
  listingId: z.string(),
  nickname: z.string(),
  city: z.string().nullable(),
  photoUrl: z.string().nullable(),
  lines: z.array(PreviewLineSchema),
});
export type PreviewTarget = z.infer<typeof PreviewTargetSchema>;

export const ActionPreviewSchema = z.object({
  /** Una riga in italiano generata dal motore, non dall'LLM. */
  summary: z.string(),
  targets: z.array(PreviewTargetSchema),
  totals: z.object({
    listings: z.number().int(),
    nights: z.number().int().optional(),
    revenueDeltaCents: z.number().int().optional(),
  }),
  warnings: z.array(PreviewWarningSchema),
});
export type ActionPreview = z.infer<typeof ActionPreviewSchema>;

export function isBlocked(preview: ActionPreview): boolean {
  return preview.warnings.some((w) => w.severity === "blocco");
}

// ── Ricevuta di sincronizzazione ─────────────────────────────────────────────

export const RECEIPT_STATUSES = [
  "inviato",
  "in_attesa",
  "confermato",
  "fallito",
] as const;
export type ReceiptStatus = (typeof RECEIPT_STATUSES)[number];

export const ReceiptChannelSchema = z.object({
  channel: ChannelSchema,
  status: z.enum(RECEIPT_STATUSES),
  at: z.string().nullable(),
  retryCount: z.number().int(),
  /** Motivo leggibile in italiano, mai il messaggio grezzo dell'API. */
  errorMessage: z.string().nullable(),
  /** Correzione proposta quando il canale ha rifiutato per un motivo noto. */
  suggestedFix: z.string().nullable(),
  /**
   * Nota neutra, né errore né conferma. Serve al caso onesto: la modifica è
   * partita ma il gestionale non espone lo stato del canale, quindi non
   * possiamo dire "confermato" e sarebbe sbagliato dire "fallito".
   */
  note: z.string().nullable().default(null),
});
export type ReceiptChannel = z.infer<typeof ReceiptChannelSchema>;

export const SyncReceiptSchema = z.object({
  actionId: z.string(),
  channels: z.array(ReceiptChannelSchema),
  /** `true` quando non resta più nulla da verificare. */
  settled: z.boolean(),
});
export type SyncReceipt = z.infer<typeof SyncReceiptSchema>;

export function receiptStatusOf(receipt: SyncReceipt): ReceiptStatus {
  if (receipt.channels.some((c) => c.status === "fallito")) return "fallito";
  if (
    receipt.channels.some(
      (c) => c.status === "in_attesa" || c.status === "inviato",
    )
  )
    return "in_attesa";
  return "confermato";
}

// ── Azione (read model verso il frontend) ────────────────────────────────────

export interface ActionView {
  id: string;
  kind: ActionKind;
  category: ActionCategory;
  status: ActionStatus;
  intentText: string | null;
  reason: string | null;
  preview: ActionPreview;
  receipt: SyncReceipt | null;
  /** Azione che ha annullato questa, se esiste. */
  rollbackActionId: string | null;
  /** Azione che questa sta annullando, se è un rollback. */
  rollbackOfActionId: string | null;
  canRollback: boolean;
  errorMessage: string | null;
  proposedAt: string;
  decidedAt: string | null;
  executedAt: string | null;
  decidedBy: string | null;
  /**
   * La conversazione di una risposta all'ospite; `null` per le altre azioni.
   *
   * Serve a ritrovare la bozza dentro la sua conversazione. Prima si cercava
   * confrontando il nome dell'ospite con quello della casa nell'anteprima, e
   * per ogni conversazione con una casa non tornava mai: «Bozza pronta» non
   * compariva quasi da nessuna parte.
   */
  conversationId: string | null;
}

// ── Utilità ──────────────────────────────────────────────────────────────────

/** Le categorie toccate da un payload: un'azione calendario può toccarne tre. */
export function categoriesOf(payload: ActionPayload): ActionCategory[] {
  switch (payload.kind) {
    case "calendario": {
      const found = new Set<ActionCategory>();
      for (const item of payload.items) {
        for (const day of item.days) {
          if (day.priceCents !== undefined) found.add("prezzo");
          if (day.minNights !== undefined) found.add("min_notti");
          if (day.status !== undefined) found.add("disponibilita");
        }
      }
      return [...found];
    }
    case "contenuto_annuncio":
      return ["contenuto_annuncio"];
    case "messaggio_ospite":
      return ["messaggio_ospite"];
    case "task":
      return ["task"];
  }
}

/** Categoria principale, quella con cui l'azione viene classificata e contata. */
export function primaryCategoryOf(payload: ActionPayload): ActionCategory {
  const categories = categoriesOf(payload);
  const priority: ActionCategory[] = [
    "prezzo",
    "disponibilita",
    "min_notti",
    "contenuto_annuncio",
    "messaggio_ospite",
    "task",
  ];
  return (
    priority.find((c) => categories.includes(c)) ?? categories[0] ?? "task"
  );
}

export function listingIdsOf(payload: ActionPayload): string[] {
  switch (payload.kind) {
    case "calendario":
    case "contenuto_annuncio":
      return [...new Set(payload.items.map((i) => i.listingId))];
    case "task":
      return [payload.listingId];
    case "messaggio_ospite":
      return [];
  }
}

/** Quante notti tocca l'azione — la metrica con cui si misura l'impatto. */
export function nightsOf(payload: ActionPayload): number {
  if (payload.kind !== "calendario") return 0;
  return payload.items.reduce((sum, item) => sum + item.days.length, 0);
}
